import React from 'react'
import cx from 'classnames'
import { animated, to } from 'react-spring'
import FullscreenOverlay from 'components/FullscreenOverlay'
import Icon from 'components/Icon'
import { useOnClickOutside } from 'hooks/click-outside'
import { useModalStack } from 'hooks/modal-stack'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { useCssTransition } from 'hooks/transitioning'
import { clamp } from 'utils/math-util'
import { mergeRefs } from 'utils/react-util'
import { IconButton } from '../IconButton'
import ScrollShadow from '../ScrollShadow'
import styles from './ModalPanel.sass'

/**
 * Modal panel context props.
 */
interface ModalPanelContextProps {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  close?: () => unknown
}

/**
 * Used to pass a ref from the `ModalPanelContent` around.
 */
export const ModalPanelContext = React.createContext<ModalPanelContextProps>({
  scrollRef: React.createRef(),
})

interface ModalPanelProps {
  children?: React.ReactNode
  showing: boolean
  navOpened?: boolean
  disableCloseOnEsc?: boolean
  disableCloseOnOutsideClick?: boolean
  onRequestClose?: () => unknown
}

/**
 * Modal panel. A full-height modal that slides into place from the right.
 */
export const ModalPanel = React.forwardRef(function ModalPanel(
  {
    showing,
    navOpened,
    onRequestClose,
    disableCloseOnEsc,
    disableCloseOnOutsideClick,
    children,
  }: ModalPanelProps,
  ref: React.Ref<HTMLDivElement>
) {
  const contentScrollRef = React.useRef<HTMLDivElement | null>(null)
  const close = onRequestClose
  const contextProps: ModalPanelContextProps = React.useMemo(
    () => ({
      scrollRef: contentScrollRef,
      close,
    }),
    [close]
  )
  const { isFirstResponder, stackDepthStyle } = useModalStack(
    {
      showing,
      close,
    },
    {
      disableCloseOnEsc,
    }
  )
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  useOnClickOutside(
    panelRef,
    isFirstResponder && !disableCloseOnOutsideClick ? close : undefined
  )

  const [onTransitionEnd, rendering, hidden] = useCssTransition(showing, true)

  return (
    rendering && (
      <FullscreenOverlay
        preventScroll
        showing={!hidden}
        onTransitionEnd={onTransitionEnd}
      >
        <ModalPanelContext.Provider value={contextProps}>
          <div
            className={cx(
              styles.container,
              navOpened && styles.navOpened,
              hidden && styles.hidden
            )}
          >
            <animated.div
              className={styles.panelContainer}
              // Refer to ModalSheet comments here.
              style={!hidden ? stackDepthStyle : undefined}
            >
              <div ref={mergeRefs([ref, panelRef])} className={styles.panel}>
                {children}
              </div>
            </animated.div>
          </div>
        </ModalPanelContext.Provider>
      </FullscreenOverlay>
    )
  )
})

interface ModalPanelHeaderProps {
  children?: React.ReactNode
  withCloseButton?: boolean
  closeButtonDataTest?: string
}

/**
 * Header for the modal panel.
 */
export function ModalPanelHeader({
  children,
  withCloseButton,
  closeButtonDataTest,
}: ModalPanelHeaderProps) {
  const { close, scrollRef } = React.useContext(ModalPanelContext)
  const scrollOffset = useScrollOffsetSpring(scrollRef)

  return (
    <div className={cx(styles.headerShell)}>
      {withCloseButton && close && (
        <div className={styles.closeButtonContainer}>
          <ModalPanelHeaderCloseButton dataTest={closeButtonDataTest} />
        </div>
      )}
      <div className={styles.headerContent}>{children}</div>
      <ScrollShadow opacity={to(scrollOffset, (v) => clamp(v / 50))} />
    </div>
  )
}

interface ModalPanelHeaderCloseButtonProps {
  dataTest?: string
}

/**
 * Close button for the modal panel header.
 */
export function ModalPanelHeaderCloseButton({
  dataTest,
}: ModalPanelHeaderCloseButtonProps) {
  const { close } = React.useContext(ModalPanelContext)

  return (
    <IconButton
      className={styles.closeButton}
      onClick={close}
      dataTest={dataTest}
    >
      <Icon name="back" size={24} />
    </IconButton>
  )
}

interface ModalPanelContentProps {
  children?: React.ReactNode
  spaced?: boolean
}

/**
 * Content container for the modal panel.
 */
export const ModalPanelContent = React.forwardRef(function ModalPanelContent(
  { children, spaced }: ModalPanelContentProps,
  ref: React.Ref<HTMLDivElement>
) {
  const { scrollRef } = React.useContext(ModalPanelContext)

  return (
    <div
      ref={mergeRefs([ref, scrollRef])}
      className={cx(styles.content, spaced && styles.spaced)}
    >
      {children}
    </div>
  )
})
